// Persistència local d'adopcions (localStorage)
// Cada adopció es guarda amb un id únic i les dates com a string dd/MM/yyyy

const STORAGE_KEY = 'gdc_adoptions_v1';

const readAll = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
};

const writeAll = (list) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
};

export const listAdoptions = () => readAll().sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

export const saveAdoption = (formData) => {
  const list = readAll();
  const record = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: Date.now(),
    // Adoptant
    nombre: formData.nombre || '',
    apellidos: formData.apellidos || '',
    telefono: formData.telefono || '',
    adoptante_email: formData.adoptante_email || '',
    // Gat
    nombre_gato: formData.nombre_gato || '',
    microchip: formData.microchip || '',
    data_naixement: formData.data_naixement || '',
    esterilitzat: formData.esterilitzat || 'no',
    // Dates
    fecha_adopcion: formData.fecha_adopcion || '',
    fecha_vacunacion: formData.fecha_vacunacion || '',
    fecha_vacunacion2: formData.fecha_vacunacion2 || '',
    fecha_esterilizacion: formData.fecha_esterilizacion || '',
    // Historial de seguiment (dates dd/MM/yyyy)
    vaccinations_history: [],
    // Còpia completa per poder regenerar el PDF idèntic
    formDataSnapshot: { ...formData },
  };
  list.push(record);
  writeAll(list);
  return record;
};

export const deleteAdoption = (id) => {
  writeAll(readAll().filter(r => r.id !== id));
};

export const updateAdoption = (id, patch) => {
  writeAll(readAll().map(r => (r.id === id ? { ...r, ...patch } : r)));
};

// Marca una nova vacunació. Es guarda a l'historial per no sobreescriure les dades del contracte.
export const markVaccinated = (id, dateDMY) => {
  writeAll(readAll().map(r => {
    if (r.id !== id) return r;
    const history = Array.isArray(r.vaccinations_history) ? r.vaccinations_history : [];
    return { ...r, vaccinations_history: [...history, dateDMY] };
  }));
};

// Marca com esterilitzat amb la data. Sobreescriu els camps del contracte.
export const markSterilized = (id, dateDMY) => {
  writeAll(readAll().map(r => (
    r.id === id ? { ...r, esterilitzat: 'si', fecha_esterilizacion: dateDMY } : r
  )));
};

export const exportAdoptions = () => {
  const data = readAll();
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const date = new Date().toISOString().split('T')[0];
  a.href = url;
  a.download = `gats-del-carrer-adopcions-${date}.json`;
  a.click();
  URL.revokeObjectURL(url);
};

export const importAdoptions = (file, { merge = true } = {}) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const imported = JSON.parse(reader.result);
        if (!Array.isArray(imported)) throw new Error('Format invàlid');
        const existing = readAll();
        const merged = merge
          ? [...existing, ...imported.filter(imp => !existing.find(e => e.id === imp.id))]
          : imported;
        writeAll(merged);
        resolve(merged.length);
      } catch (e) {
        reject(e);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

// --- Utilitats de dates ---
const parseDMY = (str) => {
  if (!str || typeof str !== 'string') return null;
  const parts = str.split('/');
  if (parts.length !== 3) return null;
  const [d, m, y] = parts.map(n => parseInt(n, 10));
  if (!d || !m || !y) return null;
  return new Date(y, m - 1, d);
};

const diffDays = (a, b) => Math.floor((a.getTime() - b.getTime()) / (1000 * 60 * 60 * 24));

const addMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};

const addYears = (date, years) => {
  const d = new Date(date);
  d.setFullYear(d.getFullYear() + years);
  return d;
};

// --- Càlcul d'estat pendent i pròxim per a una data objectiu ---
// Cada tipus (esterilització / vacunació) retorna:
//   - dueDate: data en què toca (Date o null)
//   - daysDiff: target - dueDate en dies. Positiu = retard, negatiu = queda temps
//   - pending: true si encara no està confirmat com a fet en el passat
// Regles (el veterinari decideix les dates; nosaltres només llegim el que hi ha registrat):
// STERILITZACIÓ:
//   - fet = fecha_esterilizacion existeix i és passada
//   - pendent en qualsevol altre cas (blanc, futura, o només flag esterilitzat='si' sense data confirmada)
// VACUNACIÓ:
//   - fet = hi ha una data de vacunació passada i, si toca revacunació anual (última+1any),
//     ja se n'ha registrat una de posterior
//   - pendent si no hi ha cap data, o l'única data disponible és futura, o cal revacunar (última+1any <= avui)
export const computePending = (record, targetDate) => {
  const target = targetDate instanceof Date ? targetDate : new Date(targetDate);
  const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
  const t = startOfDay(target);

  // --- STERILIZATION ---
  const birth = parseDMY(record.data_naixement);
  const sterDateRaw = parseDMY(record.fecha_esterilizacion);
  const sterDate = sterDateRaw ? startOfDay(sterDateRaw) : null;
  const declaredSterilized = record.esterilitzat === 'si';

  let sterilizationPending;
  let sterilizationDueDate = null;
  let sterilizationDaysDiff = null;
  let sterilizationIndeterminate = false;

  if (sterDate && sterDate < t) {
    // Data passada confirmada → fet
    sterilizationPending = false;
  } else if (sterDate && sterDate >= t) {
    // Data futura programada → pendent (encara no s'ha fet)
    sterilizationPending = true;
    sterilizationDueDate = sterDate;
    sterilizationDaysDiff = diffDays(t, sterDate); // negatiu = en el futur
  } else {
    // Data en blanc
    sterilizationPending = true;
    if (birth && !declaredSterilized) {
      // Referència orientativa (6 mesos) només per ordenar; sempre pendent
      sterilizationDueDate = addMonths(birth, 6);
      sterilizationDaysDiff = diffDays(t, startOfDay(sterilizationDueDate));
    } else {
      sterilizationIndeterminate = true;
    }
  }

  // --- VACCINATION ---
  const vac1 = parseDMY(record.fecha_vacunacion);
  const vac2 = parseDMY(record.fecha_vacunacion2);
  const historyDates = (record.vaccinations_history || []).map(parseDMY).filter(Boolean);
  const allDates = [vac1, vac2, ...historyDates].filter(Boolean).map(startOfDay);
  const pastDates = allDates.filter(d => d < t).sort((a, b) => b - a);
  const futureDates = allDates.filter(d => d >= t).sort((a, b) => a - b);
  const lastPastVac = pastDates[0] || null;
  const nextFutureVac = futureDates[0] || null;

  let vaccinationPending;
  let vaccinationDueDate = null;
  let vaccinationDaysDiff = null;
  let vaccinationIndeterminate = false;

  if (!lastPastVac && !nextFutureVac) {
    // Cap data registrada → pendent
    vaccinationPending = true;
    vaccinationIndeterminate = true;
  } else if (nextFutureVac && !lastPastVac) {
    // Només data futura programada → pendent
    vaccinationPending = true;
    vaccinationDueDate = nextFutureVac;
    vaccinationDaysDiff = diffDays(t, nextFutureVac);
  } else if (lastPastVac) {
    const annualDue = startOfDay(addYears(lastPastVac, 1));
    if (t >= annualDue) {
      // Toca revacunació anual
      vaccinationPending = true;
      vaccinationDueDate = nextFutureVac || annualDue;
      vaccinationDaysDiff = diffDays(t, startOfDay(vaccinationDueDate));
    } else if (nextFutureVac) {
      // Encara no toca anual, però el veterinari ha programat una futura
      vaccinationPending = true;
      vaccinationDueDate = nextFutureVac;
      vaccinationDaysDiff = diffDays(t, nextFutureVac);
    } else {
      // Al dia i sense programades
      vaccinationPending = false;
    }
  }

  const sterilizationOverdueDays = sterilizationPending && sterilizationDaysDiff != null && sterilizationDaysDiff > 0 ? sterilizationDaysDiff : 0;
  const vaccinationOverdueDays = vaccinationPending && vaccinationDaysDiff != null && vaccinationDaysDiff > 0 ? vaccinationDaysDiff : 0;

  return {
    sterilizationDueDate,
    sterilizationDaysDiff,
    sterilizationIndeterminate,
    vaccinationDueDate,
    vaccinationDaysDiff,
    vaccinationIndeterminate,
    sterilizationPending,
    sterilizationOverdueDays,
    vaccinationPending,
    vaccinationOverdueDays,
    lastVaccinationDate: lastPastVac || nextFutureVac,
    sterilizationDate: sterDateRaw,
  };
};


// --- Export CSV compatible amb Excel (UTF-8 amb BOM, separador ";") ---
const csvEscape = (val) => {
  const s = val == null ? '' : String(val);
  if (/[";\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const fmtDate = (d) => (d instanceof Date ? `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}` : '');

// items: registres enriquits amb els camps de computePending
export const exportPendingCSV = (items, headers) => {
  const cols = [
    'nombre_gato', 'microchip', 'adopter', 'telefono', 'email',
    'sterilization_status', 'sterilization_due', 'sterilization_days',
    'vaccination_status', 'vaccination_due', 'vaccination_days',
    'last_vaccination', 'sterilization_date', 'data_naixement', 'fecha_adopcion',
  ];
  const head = cols.map(c => csvEscape(headers[c] || c)).join(';');

  const statusFor = (dueDate, daysDiff, indeterminate) => {
    if (indeterminate) return headers.status_indeterminate;
    if (dueDate == null) return headers.status_done;
    if (daysDiff > 0) return headers.status_overdue;
    if (daysDiff === 0) return headers.status_today;
    return headers.status_upcoming;
  };

  const rows = items.map(r => [
    r.nombre_gato,
    r.microchip,
    `${r.nombre || ''} ${r.apellidos || ''}`.trim(),
    r.telefono,
    r.adoptante_email,
    statusFor(r.sterilizationDueDate, r.sterilizationDaysDiff, r.sterilizationIndeterminate),
    fmtDate(r.sterilizationDueDate),
    r.sterilizationDaysDiff != null ? r.sterilizationDaysDiff : '',
    statusFor(r.vaccinationDueDate, r.vaccinationDaysDiff, r.vaccinationIndeterminate),
    fmtDate(r.vaccinationDueDate),
    r.vaccinationDaysDiff != null ? r.vaccinationDaysDiff : '',
    fmtDate(r.lastVaccinationDate),
    fmtDate(r.sterilizationDate) || (r.esterilitzat === 'si' ? headers.yes : ''),
    r.data_naixement || '',
    r.fecha_adopcion || '',
  ].map(csvEscape).join(';'));

  const csv = '\uFEFF' + [head, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const date = new Date().toISOString().split('T')[0];
  a.href = url;
  a.download = `gats-pendents-${date}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};
